// theme/tokens.js
//
// Single source of truth for the brand's visual language: colors, type
// scale, radii, shadows, and motion. Every component should import from
// here instead of redeclaring its own local `tokens`/`BRAND` object.

export const tokens = {
  color: {
    teal: "#0F8B8D",
    tealDark: "#0A5F60",
    tealLight: "rgba(15,138,141,0.12)",

    ink: "#12242E",
    inkMuted: "rgba(18,36,46,0.65)",
    inkFaint: "rgba(18,36,46,0.45)",
    line: "rgba(18,36,46,0.10)",

    surface: "#FFFFFF",
    surfaceAlt: "#F7F8FA",
    surfaceMuted: "#F5F7F8",
    overlay: "rgba(18,36,46,0.48)",

    success: "#1E8E5A",
    successBg: "rgba(30,142,90,0.10)",
    warning: "#B98900",
    warningBg: "rgba(185,137,0,0.10)",
    error: "#C0392B",
    errorBg: "rgba(192,57,43,0.10)",
    info: "#0F6FB9",
    infoBg: "rgba(15,111,185,0.10)",

    white: "#FFFFFF",
    black: "#000000",
  },

  font: {
    heading: "'Sora', sans-serif",
    body: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },

  type: {
    display: { fontFamily: "heading", fontSize: "3rem", lineHeight: 1.15, fontWeight: 700, letterSpacing: "-0.02em" },
    h1: { fontFamily: "heading", fontSize: "2.5rem", lineHeight: 1.2, fontWeight: 700, letterSpacing: "-0.01em" },
    h2: { fontFamily: "heading", fontSize: "2rem", lineHeight: 1.25, fontWeight: 700, letterSpacing: "-0.01em" },
    h3: { fontFamily: "heading", fontSize: "1.5rem", lineHeight: 1.3, fontWeight: 700 },
    h4: { fontFamily: "heading", fontSize: "1.25rem", lineHeight: 1.35, fontWeight: 700 },
    h5: { fontFamily: "heading", fontSize: "1.125rem", lineHeight: 1.4, fontWeight: 600 },
    h6: { fontFamily: "heading", fontSize: "1rem", lineHeight: 1.4, fontWeight: 600 },
    subtitle1: { fontFamily: "body", fontSize: "1rem", lineHeight: 1.5, fontWeight: 600 },
    subtitle2: { fontFamily: "body", fontSize: "0.875rem", lineHeight: 1.5, fontWeight: 600 },
    body1: { fontFamily: "body", fontSize: "1rem", lineHeight: 1.65, fontWeight: 400 },
    body2: { fontFamily: "body", fontSize: "0.875rem", lineHeight: 1.6, fontWeight: 400 },
    caption: { fontFamily: "body", fontSize: "0.75rem", lineHeight: 1.5, fontWeight: 400 },
    overline: { fontFamily: "body", fontSize: "0.75rem", lineHeight: 1.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" },
    button: { fontFamily: "body", fontSize: "0.875rem", lineHeight: 1.5, fontWeight: 600, textTransform: "none" },
  },

  textOn: {
    light: { primary: "#12242E", secondary: "rgba(18,36,46,0.65)", disabled: "rgba(18,36,46,0.38)", brand: "#0F8B8D" },
    dark: { primary: "#FFFFFF", secondary: "rgba(255,255,255,0.72)", disabled: "rgba(255,255,255,0.40)", brand: "#5FD1D3" },
  },

  bg: {
    page: "#F7F8FA",
    surface: "#FFFFFF",
    surfaceMuted: "#F5F7F8",
    brand: "#0F8B8D",
    brandDark: "#0A5F60",
    inkSection: "#12242E",
  },

  radius: { sm: 8, md: 12, lg: 16, xl: 24, pill: 999 },

  shadow: {
    sm: "0 2px 10px rgba(18,36,46,0.05)",
    md: "0 10px 24px rgba(18,36,46,0.10)",
    lg: "0 20px 44px rgba(18,36,46,0.16)",
  },

  button: {
    height: { sm: 32, md: 40, lg: 48 },
    paddingX: { sm: 14, md: 20, lg: 28 },
    fontSize: { sm: "0.8125rem", md: "0.875rem", lg: "0.9375rem" },
    radius: 8,
  },

  input: {
    height: { sm: 36, md: 44, lg: 52 },
    fontSize: { sm: "0.8125rem", md: "0.875rem", lg: "0.9375rem" },
    radius: 10,
    bg: "#FFFFFF",
    bgMuted: "#F5F7F8",
    placeholder: "rgba(18,36,46,0.45)",
    border: "rgba(18,36,46,0.10)",
    borderHover: "rgba(18,36,46,0.24)",
    borderFocus: "#0F8B8D",
    borderError: "#C0392B",
    label: "rgba(18,36,46,0.65)",
  },

  banner: {
    primary: { background: "linear-gradient(135deg, #0F8B8D 0%, #0A5F60 100%)", text: "#FFFFFF", textMuted: "rgba(255,255,255,0.78)" },
    dark: { background: "#12242E", text: "#FFFFFF", textMuted: "rgba(255,255,255,0.68)" },
    light: { background: "#F7F8FA", text: "#12242E", textMuted: "rgba(18,36,46,0.65)" },
  },

  card: {
    padding: { sm: 16, md: 24 },
    radius: 12,
    elevated: { background: "#FFFFFF", border: "1px solid rgba(18,36,46,0.10)", shadowResting: "0 2px 10px rgba(18,36,46,0.05)", shadowHover: "0 10px 24px rgba(18,36,46,0.10)", borderHover: "#0F8B8D" },
    outlined: { background: "#FFFFFF", border: "1px dashed rgba(18,36,46,0.16)", shadowResting: "none", shadowHover: "none", borderHover: "rgba(18,36,46,0.24)" },
    flat: { background: "transparent", border: "none", shadowResting: "none", shadowHover: "none", borderHover: "transparent" },
  },

  space: (multiplier = 1) => `${multiplier * 8}px`,
};

export const fadeInUp = {
  "@keyframes fadeInUp": { from: { opacity: 0, transform: "translateY(16px)" }, to: { opacity: 1, transform: "translateY(0)" } },
};

export const fadeIn = {
  "@keyframes fadeIn": { from: { opacity: 0 }, to: { opacity: 1 } },
};
